import React from 'react';
import { animated, useSprings } from 'react-spring';
import styled from 'styled-components';
import History from '../History/History';
import { useSelector } from 'react-redux';
import { isRoomConfigurationActive } from 'ducks/layout';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { isMobile } from 'utils/platform';
import useIsRtl from 'hooks/useIsRtl';
import { getFeatureComponents } from 'components/Features/Features';
import { RenderLocation } from 'features/Feature';
import { useIntl } from 'react-intl';

const Position = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  margin-bottom: ${({ theme }) => theme.space.md};
  padding-left: 1.25rem;
  left: 0;

  &[data-rtl='true'] {
    flex-direction: row-reverse;
  }
`;

const Menu = styled.div`
  position: absolute;
  z-index: 0;
  bottom: 0;
  left: 0.1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column-reverse;
`;

const HistoryButtons = styled(History)`
  position: absolute;
  bottom: 0.5rem;
  left: 3.75rem;
`;

const MenuButton = styled(animated.div)`
  position: relative;
  margin-bottom: 0.5rem;
`;

export type SceneAction = (
  props: React.PropsWithoutRef<JSX.IntrinsicElements['button']>
) => JSX.Element;

const SceneActions = () => {
  const { plannerConfig } = usePlannerConfig();
  const { formatMessage } = useIntl();

  const items = plannerConfig.sceneActions.concat(
    getFeatureComponents(plannerConfig.features, RenderLocation.Action)
  );

  const springs = useSprings(
    items.length,
    items.map((_, index) => ({
      opacity: 1,
      y: 0,
      zIndex: items.length - index,
      config: {
        mass: 1,
        tension: 300,
        friction: 25,
        clamp: true
      }
    }))
  );

  return (
    <Menu role="menu" aria-label={formatMessage({ id: 'storageone.sceneActionsMenu' })}>
      {springs.map((props: any, index: number) => {
        const Component = items[index];
        return (
          <MenuButton
            style={{
              zIndex: props.zIndex,
              transform: props.y.to((y: number) => `translateY(${y}%)`),
              opacity: props.opacity
            }}
            key={index}
          >
            <Component />
          </MenuButton>
        );
      })}
    </Menu>
  );
};

const SceneMenu = () => {
  const rtl = useIsRtl();
  const roomConfigurationActive = useSelector(isRoomConfigurationActive);
  const items = [SceneActions, HistoryButtons];

  const animationConfig = {
    mass: 1,
    tension: 180,
    friction: 25,
    clamp: true
  };
  const springs = useSprings(
    items.length,
    items.map((_, index) => ({
      opacity: index === 0 && roomConfigurationActive ? 0 : 1,
      x: roomConfigurationActive ? -20 : 0,
      config: animationConfig
    }))
  );

  if (isMobile()) {
    return null;
  }

  return (
    <Position data-rtl={rtl}>
      {springs.map((style, index) => {
        const Component = items[index];
        return (
          <animated.div
            style={{
              opacity: style.opacity,
              transform: style.x.to((x: any) => `translateX(${x}rem)`),
              // Without z-index 1 the measurements action button tooltip will be hidden under the history menu
              zIndex: index === 0 ? 1 : 0
            }}
            key={index}
          >
            <Component />
          </animated.div>
        );
      })}
    </Position>
  );
};

export default SceneMenu;
